/**
 * WebAuthn Helper Functions
 * 
 * Utilities for passkey registration and authentication
 */

/**
 * Check if WebAuthn is supported in the current browser
 */
export function isWebAuthnSupported(): boolean {
    return !!(window.PublicKeyCredential && navigator.credentials);
}

/**
 * Check if platform authenticator is available (e.g., Touch ID, Face ID, Windows Hello)
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
    if (!isWebAuthnSupported()) {
        return false;
    }

    try {
        return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (e) {
        return false;
    }
}

/**
 * Convert base64url string to ArrayBuffer
 */
export function base64urlDecode(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64url string
 */
export function base64urlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Register a new passkey
 */
export async function registerPasskey(options: any): Promise<any> {
    if (!isWebAuthnSupported()) {
        throw new Error('WebAuthn is not supported in this browser');
    }

    // Convert base64url strings to ArrayBuffers
    const publicKeyOptions = {
        ...options,
        challenge: base64urlDecode(options.challenge),
        user: {
            ...options.user,
            id: base64urlDecode(options.user.id),
        },
        excludeCredentials: options.excludeCredentials?.map((cred: any) => ({
            ...cred,
            id: base64urlDecode(cred.id),
        })) || [],
    };

    try {
        const credential = await navigator.credentials.create({
            publicKey: publicKeyOptions,
        }) as PublicKeyCredential;

        if (!credential) {
            throw new Error('Failed to create credential');
        }

        const response = credential.response as AuthenticatorAttestationResponse;

        // Extract credential data
        return {
            credentialId: base64urlEncode(credential.rawId),
            publicKey: base64urlEncode(response.getPublicKey()!),
            challenge: options.challenge,
            aaguid: extractAAGUID(response.getAuthenticatorData()),
        };
    } catch (error: any) {
        if (error.name === 'NotAllowedError') {
            throw new Error('User cancelled passkey registration');
        } else if (error.name === 'InvalidStateError') {
            throw new Error('This passkey is already registered');
        }
        throw error;
    }
}

/**
 * Authenticate with a passkey
 */
export async function authenticateWithPasskey(options: any): Promise<any> {
    if (!isWebAuthnSupported()) {
        throw new Error('WebAuthn is not supported in this browser');
    }

    // Convert base64url strings to ArrayBuffers
    const publicKeyOptions = {
        ...options,
        challenge: base64urlDecode(options.challenge),
        allowCredentials: options.allowCredentials?.map((cred: any) => ({
            ...cred,
            id: base64urlDecode(cred.id),
        })) || [],
    };

    try {
        const credential = await navigator.credentials.get({
            publicKey: publicKeyOptions,
        }) as PublicKeyCredential;

        if (!credential) {
            throw new Error('Failed to get credential');
        }

        const response = credential.response as AuthenticatorAssertionResponse;

        // Extract authentication data
        return {
            credentialId: base64urlEncode(credential.rawId),
            authenticatorData: base64urlEncode(response.authenticatorData),
            clientDataJSON: base64urlEncode(response.clientDataJSON),
            signature: base64urlEncode(response.signature),
            challenge: options.challenge,
            counter: extractCounter(response.authenticatorData),
        };
    } catch (error: any) {
        if (error.name === 'NotAllowedError') {
            throw new Error('User cancelled passkey authentication');
        }
        throw error;
    }
}

/**
 * Authenticate with passkey autofill (conditional UI)
 */
export async function authenticateWithPasskeyAutofill(options: any): Promise<any> {
    if (!isWebAuthnSupported()) {
        throw new Error('WebAuthn is not supported in this browser');
    }

    // Check if conditional mediation is supported
    if (!PublicKeyCredential.isConditionalMediationAvailable ||
        !(await PublicKeyCredential.isConditionalMediationAvailable())) {
        // Fallback to regular authentication
        return authenticateWithPasskey(options);
    }

    const publicKeyOptions = {
        ...options,
        challenge: base64urlDecode(options.challenge),
    };

    try {
        const credential = await navigator.credentials.get({
            publicKey: publicKeyOptions,
            mediation: 'conditional' as CredentialMediationRequirement,
        }) as PublicKeyCredential;

        if (!credential) {
            throw new Error('Failed to get credential');
        }

        const response = credential.response as AuthenticatorAssertionResponse;

        return {
            credentialId: base64urlEncode(credential.rawId),
            authenticatorData: base64urlEncode(response.authenticatorData),
            clientDataJSON: base64urlEncode(response.clientDataJSON),
            signature: base64urlEncode(response.signature),
            challenge: options.challenge,
            counter: extractCounter(response.authenticatorData),
        };
    } catch (error: any) {
        if (error.name === 'NotAllowedError') {
            throw new Error('User cancelled passkey authentication');
        }
        throw error;
    }
}

/**
 * Extract AAGUID from authenticator data
 */
function extractAAGUID(authenticatorData: ArrayBuffer): string | null {
    try {
        const dataView = new DataView(authenticatorData);
        // AAGUID is at bytes 37-52 (16 bytes)
        const aaguidBytes = new Uint8Array(authenticatorData.slice(37, 53));
        const hex = Array.from(aaguidBytes).map(b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch (e) {
        return null;
    }
}

/**
 * Extract signature counter from authenticator data
 */
function extractCounter(authenticatorData: ArrayBuffer): number {
    try {
        const dataView = new DataView(authenticatorData);
        // Counter is at bytes 33-36 (4 bytes, big-endian)
        return dataView.getUint32(33, false);
    } catch (e) {
        return 0;
    }
}
